import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ProgressBar, Spinner } from 'react-bootstrap';
import {
    Dialog,
    DialogContent,
    makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';

const STEP_STATUS = {
    PENDING: 'pending',
    RUNNING: 'running',
    DONE: 'done',
    ERROR: 'error',
};

const useStyles = makeStyles((theme) => ({
    dialog: {
        width: 480,
        maxWidth: '95vw',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(3),
    },
    stepList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    step: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    success: {
        color: theme.palette.success.main,
    },
    error: {
        color: theme.palette.error.main,
    },
    pending: {
        color: theme.palette.grey[400],
    },
}));

const MergeProgressModal = ({
    open, secondaries, status, errorMessage, onClose,
}) => {
    const classes = useStyles();
    const [steps, setSteps] = useState([]);
    const canClose = status === 'error';

    useEffect(() => {
        setSteps(secondaries.map((secondary) => ({
            customerCode: secondary.customerCode,
            label: `${secondary.firstName || ''} ${secondary.lastName || ''}`.trim() || `Customer ${secondary.customerCode}`,
            status: STEP_STATUS.PENDING,
        })));
    }, [secondaries]);

    useEffect(() => {
        if (!open) return;

        if (status === 'running') {
            setSteps((prev) => prev.map((step, index) => ({
                ...step,
                status: index === 0 ? STEP_STATUS.RUNNING : STEP_STATUS.PENDING,
            })));
            return;
        }

        const statusMap = {
            success: STEP_STATUS.DONE,
            error: STEP_STATUS.ERROR,
        };

        const stepStatus = statusMap[status];

        if (stepStatus) {
            setSteps((prev) => prev.map((step) => ({
                ...step,
                status: stepStatus,
            })));
        }
    }, [open, status]);

    const completedCount = steps.filter((step) => step.status === STEP_STATUS.DONE).length;
    const progress = steps.length === 0
        ? 0
        : Math.round((completedCount / steps.length) * 100);

    const renderIcon = (stepStatus) => {
        if (stepStatus === STEP_STATUS.DONE) {
            return <CheckCircleIcon className={classes.success} fontSize="small" />;
        }
        if (stepStatus === STEP_STATUS.ERROR) {
            return <ErrorIcon className={classes.error} fontSize="small" />;
        }
        if (stepStatus === STEP_STATUS.RUNNING) {
            return <Spinner animation="border" size="sm" variant="primary" />;
        }
        return (
            <span
                className={classes.pending}
                style={{
                    display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid currentColor',
                }}
            />
        );
    };

    const statusMessage = () => {
        if (status === 'success') return 'Merge completed successfully.';
        if (status === 'error') return 'Merge failed. You can close this dialog and try again.';
        return 'Please wait while records are merged...';
    };

    const progressValue = status === 'running' && progress === 0 ? 15 : progress;

    const progressVariant = () => {
        if (status === 'error') return 'danger';
        if (status === 'success') return 'success';
        return 'primary';
    };

    return (
        <Dialog
            open={open}
            disableBackdropClick={!canClose}
            disableEscapeKeyDown={!canClose}
            onClose={canClose ? onClose : undefined}
            PaperProps={{ className: classes.dialog }}
        >
            <DialogAppBar
                title="Merging Customers"
                iconSize="sm"
                onClose={onClose}
                disabled={!canClose}
            />
            <DialogContent className={classes.body}>
                <div>
                    {statusMessage()}
                </div>
                <ProgressBar
                    now={progressValue}
                    variant={progressVariant()}
                    animated={status === 'running'}
                />
                <div className={classes.stepList}>
                    {steps.map((step) => (
                        <div key={step.customerCode} className={classes.step}>
                            {renderIcon(step.status)}
                            <span style={{ fontSize: '0.875rem' }}>
                                {`Merging ${step.label}`}
                            </span>
                        </div>
                    ))}
                </div>
                {status === 'error' && errorMessage && (
                    <div className={classes.error} style={{ fontSize: '0.875rem' }}>
                        {errorMessage}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

MergeProgressModal.propTypes = {
    open: PropTypes.bool.isRequired,
    secondaries: PropTypes.arrayOf(PropTypes.object).isRequired,
    status: PropTypes.oneOf(['running', 'success', 'error']).isRequired,
    errorMessage: PropTypes.string,
    onClose: PropTypes.func,
};

MergeProgressModal.defaultProps = {
    errorMessage: '',
    onClose: () => {},
};

export default MergeProgressModal;
